import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiBook, FiCalendar, FiAward, FiX, FiCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { trainingAPI } from '../../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';

const RESULT_SUGGESTIONS = ['ผ่าน', 'ไม่ผ่าน', 'รอผล', 'กำลังอบรม'];
const MEMO_SUGGESTIONS = ['หลักสูตรบังคับ', 'เก็บชั่วโมง', 'สอบแก้ตัว', 'ออนไลน์'];

const TrainingManager = ({ agentId, isEditable = true, showTitle = true }) => {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form State
  const initialFormState = {
    courseName: '',
    institute: '',
    startDate: '',
    endDate: '',
    result: '',
    memo: ''
  };
  const [formData, setFormData] = useState(initialFormState);
  
  // Delete Dialog State
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (agentId) {
      fetchTrainings();
    }
  }, [agentId]);

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const { data } = await trainingAPI.getTrainingsByAgent(agentId);
      setTrainings(data || []);
    } catch (error) {
      console.error('Error fetching trainings:', error);
      toast.error('ไม่สามารถดึงข้อมูลการอบรมได้');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData(initialFormState);
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setFormData({
      courseName: item.courseName,
      institute: item.institute || '',
      startDate: item.startDate ? item.startDate.split('T')[0] : '',
      endDate: item.endDate ? item.endDate.split('T')[0] : '',
      result: item.result || '',
      memo: item.memo || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        console.log(`Updating training id=${editingItem.id} for agentId=${agentId}`);
        // Update
        const updateData = {
          ...formData,
          agentId: parseInt(agentId)
        };
        // Clean empty dates to null if needed, but backend handles nullable
        if (!updateData.startDate) updateData.startDate = null;
        if (!updateData.endDate) updateData.endDate = null;

        await trainingAPI.updateTraining(editingItem.id, updateData);
        toast.success('อัปเดตข้อมูลเรียบร้อย');
      } else {
        console.log(`Creating new training for agentId=${agentId}`);
        // Create
        const createData = {
          ...formData,
          agentId: parseInt(agentId)
        };
        console.log('Create Data:', createData);
        if (!createData.startDate) createData.startDate = null;
        if (!createData.endDate) createData.endDate = null;

        await trainingAPI.createTraining(createData);
        toast.success('เพิ่มข้อมูลเรียบร้อย');
      }
      setShowModal(false);
      fetchTrainings();
    } catch (error) {
      console.error('Error saving training:', error);
      toast.error('ไม่สามารถบันทึกข้อมูลได้');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await trainingAPI.deleteTraining(deleteId);
      toast.success('ลบข้อมูลเรียบร้อย');
      fetchTrainings();
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting training:', error);
      toast.error('ไม่สามารถลบข้อมูลได้');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading && trainings.length === 0) {
    return <div className="py-4"><LoadingSpinner /></div>;
  }
  
  const displayedTrainings = isExpanded ? trainings : trainings.slice(0, 3);

  return (
    <div className="space-y-4">
      {(showTitle || isEditable) && (
        <div className="flex justify-between items-center mb-4">
          {showTitle ? (
            <div className="font-semibold text-gray-900 flex items-center">
              <SafeIcon icon={FiBook} className="mr-2 text-indigo-500" />
              ประวัติการอบรม ({trainings.length})
            </div>
          ) : (
            <div></div> // Spacer if title hidden but edit button needed
          )}
          
          {isEditable && (
            <button
              type="button"
              onClick={openAddModal}
              className="flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
              เพิ่มรายการ
            </button>
          )}
        </div>
      )}

      <div className="space-y-4">
        {trainings.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
            ยังไม่มีข้อมูลการอบรม
          </p>
        ) : (
          displayedTrainings.map((item) => (
            <div key={item.id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900 text-base mb-1">{item.courseName}</h5>
                  
                  <div className="flex flex-wrap gap-y-1 gap-x-4 text-sm text-gray-600 mb-2">
                    {item.institute && (
                      <span className="flex items-center">
                         <span className="font-medium mr-1">สถาบัน:</span> {item.institute}
                      </span>
                    )}
                    {(item.startDate || item.endDate) && (
                      <span className="flex items-center text-gray-500">
                        <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5 mr-1" />
                        {item.startDate ? formatDate(item.startDate) : '?'} 
                        {' - '} 
                        {item.endDate ? formatDate(item.endDate) : '?'}
                      </span>
                    )}
                  </div>
                  
                  {item.result && (
                     <div className="flex items-center text-sm text-green-700 bg-green-50 px-2 py-1 rounded w-fit mb-2">
                        <SafeIcon icon={FiAward} className="w-3.5 h-3.5 mr-1" />
                        <span className="font-medium mr-1">ผลการอบรม:</span> {item.result}
                     </div>
                  )}

                  {item.memo && (
                    <p className="text-sm text-gray-500 italic mt-1 border-l-2 border-gray-200 pl-2">
                      {item.memo}
                    </p>
                  )}
                </div>

                {isEditable && (
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => openEditModal(item)}
                      className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                      title="แก้ไข"
                    >
                      <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteId(item.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      title="ลบ"
                    >
                      <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {trainings.length > 3 && (
        <div className="text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 flex items-center justify-center w-full"
          >
            {isExpanded ? 'แสดงน้อยลง' : 'แสดงทั้งหมด'}
            <SafeIcon icon={isExpanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 ml-1" />
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setShowModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      {editingItem ? 'แก้ไขข้อมูลการอบรม' : 'เพิ่มข้อมูลการอบรม'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                    >
                      <SafeIcon icon={FiX} className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อหลักสูตร <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="courseName"
                        required
                        value={formData.courseName}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="เช่น FA Standard, Unit Link"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">สถาบัน / สถานที่อบรม</label>
                      <input
                        type="text"
                        name="institute"
                        value={formData.institute}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="ระบุสถาบัน (ถ้ามี)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">วันที่เริ่ม</label>
                        <input
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สิ้นสุด</label>
                        <input
                          type="date"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">ผลการอบรม</label>
                      <input
                        type="text"
                        name="result"
                        value={formData.result}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="เช่น ผ่าน, กำลังศึกษา"
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {RESULT_SUGGESTIONS.map(res => (
                          <button
                            key={res}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, result: res }))}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            {res}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">บันทึกเพิ่มเติม</label>
                      <textarea
                        name="memo"
                        rows={3}
                        value={formData.memo}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="รายละเอียดเพิ่มเติม..."
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {MEMO_SUGGESTIONS.map(memo => (
                          <button
                            key={memo}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, memo }))}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                          >
                            {memo}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    บันทึก
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    ยกเลิก
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="ลบข้อมูลการอบรม"
        message="คุณแน่ใจหรือไม่ที่จะลบข้อมูลการอบรมนี้? การกระทำนี้ไม่สามารถเรียกคืนได้"
        confirmText="ลบข้อมูล"
        cancelText="ยกเลิก"
        type="danger"
      />
    </div>
  );
};

export default TrainingManager;
